import React, {createContext, useContext, useEffect, useState} from 'react';
import authService from '../../services/authService';

const AuthContext = createContext(null);

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth muss innerhalb von AuthProvider verwendet werden');
    }
    return context;
};

export const AuthProvider = ({children}) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Prüfe, ob User bereits eingeloggt ist
        const initAuth = async () => {
            try {
                const token = authService.getToken();
                const cachedUser = authService.getCurrentUser();

                // Wenn Token UND User vorhanden sind, sofort laden
                if (token && cachedUser) {
                    setUser(cachedUser);
                    setLoading(false);

                    // Optional: Im Hintergrund frische Daten holen (ohne await)
                    authService.getCurrentUserFromServer()
                        .then(freshUserData => {
                            setUser(freshUserData);
                        })
                        .catch(() => {
                            // Bei Fehler einfach gecachte Daten behalten
                            console.warn('Fehler beim Aktualisieren der Benutzerdaten im Hintergrund');
                        });
                } else {
                    // Kein Token oder User - nicht eingeloggt
                    setLoading(false);
                }
            } catch (error) {
                console.error('Fehler bei der Initialisierung der Authentifizierung:', error);
                setLoading(false);
            }
        };

        initAuth();
    }, []);

    const login = async (email, password) => {
        const data = await authService.login(email, password);
        const userData = {
            id: data.id,
            email: data.email,
            firstName: data.firstName,
            lastName: data.lastName,
            role: data.role
        };
        // User sofort setzen
        setUser(userData);
        // Sicherstellen dass es in localStorage ist
        localStorage.setItem('user', JSON.stringify(userData));
        return data;
    };

    const register = async (email, password, firstName, lastName, unitId = null) => {
        const data = await authService.register(email, password, firstName, lastName, unitId);
        const userData = {
            id: data.id,
            email: data.email,
            firstName: data.firstName,
            lastName: data.lastName,
            role: data.role
        };
        // User sofort setzen
        setUser(userData);
        // Sicherstellen dass es in localStorage ist
        localStorage.setItem('user', JSON.stringify(userData));
        return data;
    };

    const logout = () => {
        authService.logout();
        setUser(null);
    };

    const refreshUser = async () => {
        try {
            const userData = await authService.getCurrentUserFromServer();
            setUser(userData);
            return userData;
        } catch (error) {
            logout();
            throw error;
        }
    };

    // Rollen-Hilfsfunktionen
    const hasRole = (roles) => {
        if (!user || !user.role) return false;
        return roles.includes(user.role);
    };

    const isAdmin = () => hasRole(['ADMIN']);
    const isDistrictYouthLeader = () => hasRole(['DISTRICT_YOUTH_LEADER', 'ADMIN']);
    const isYouthLeader = () => hasRole(['YOUTH_LEADER', 'DISTRICT_YOUTH_LEADER', 'ADMIN']);
    const isSupervisor = () => hasRole(['SUPERVISOR', 'YOUTH_LEADER', 'DISTRICT_YOUTH_LEADER', 'ADMIN']);
    const canManageAll = () => hasRole(['ADMIN', 'DISTRICT_YOUTH_LEADER']);

    const value = {
        user,
        loading,
        login,
        register,
        logout,
        refreshUser,
        isAuthenticated: !!user,
        hasRole,
        isAdmin,
        isDistrictYouthLeader,
        isYouthLeader,
        isSupervisor,
        canManageAll,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
